import type { CopyTree } from '@/types/copy';

import {
	formatEffectiveDate,
	formatUnitPrice,
	formatUsd,
	formatWholeUsd,
} from '@/shared/utils/format-money';

import { UNIT_PRICES_USD } from './unit-prices';

export const FIRST_NAME_INFO =
	"This will be replaced with the candidate's first name before sending.";

export const ASSIGNMENT_LINK_INFO =
	"This will be replaced with each candidate's own assignment link when you send an assignment.";

const CREDIT_USAGE_TOOLTIP = 'Credits are needed for AI usage, sending emails and offer letters.';
const STAGE_CHANGES_TOOLTIP =
	'Move unlimited candidates across different stages of your hiring funnel';

/**
 * Central registry for user-facing copy.
 *
 * Rules:
 * - Values are data, never JSX. `<CopyText>` renders them.
 * - Plan names come from the API's `display_label`; never render a `plan_type` enum.
 * - Usage is spoken of as "credits" — a shared monthly credit limit across email, AI
 *   review and LinkedIn. The older "allowance"/dollars-only wording is retired.
 * - Money always goes through `formatUsd` — never inline `toFixed`.
 * - Copy that varies by plan or role is a sibling key, not a branch in the renderer.
 */
export const COPY = {
	limits: {
		// One nudge for every action — they all draw on the same credit balance.
		// Role is a sibling key (`*Referrer`), never a branch in the renderer.
		exhausted: {
			title: 'Monthly credit limit reached',
			// Plain text, not an inline action: the nudge already has an Upgrade button beside it.
			description: 'Upgrade to continue using AI, sending emails and offer letters.',
		},
		exhaustedReferrer: {
			title: 'Monthly credit limit reached',
			description: 'Ask your workspace admin to upgrade.',
		},

		// Body-copy variants for slots that take a sentence, not a title.
		exhaustedBody: 'You have run out of credits.',
		/** Agent limit banners. Email draws on the same credits, so it is not a
		 * separate cap; agent invocations remain their own limit. */
		agentInvocations: "You've hit your AI agent usage limit.",
		agentGeneric: "You've hit a usage limit.",
		/** A raw 429 (too many requests), not a credit problem — hence "try again". */
		sendingRateLimited: 'You have hit your sending limit. Please try again later.',
		exhaustedReviewPaused: 'You have run out of credits. Review paused.',

		interviews: {
			title: 'Monthly interview limit reached',
			description: 'Upgrade your plan for unlimited interviews.',
		},
		interviewsReferrer: {
			title: 'Monthly interview limit reached',
			description: 'Ask your workspace admin to upgrade for unlimited interviews.',
		},

		// Starter still caps assignments, so this asks for "more", not "unlimited".
		assignments: {
			title: 'Monthly assignment limit reached',
			description: 'Upgrade your plan to send more assignments.',
		},
		assignmentsReferrer: {
			title: 'Monthly assignment limit reached',
			description: 'Ask your workspace admin to upgrade to send more assignments.',
		},

		// Every plan below Pro caps offer letters, so this asks for "more", not "unlimited".
		offers: {
			title: 'Monthly offer letter limit reached',
			description: 'Upgrade your plan to send more offer letters.',
		},
		offersReferrer: {
			title: 'Monthly offer letter limit reached',
			description: 'Ask your workspace admin to upgrade to send more offer letters.',
		},

		// Only Free caps stage changes; every paid plan is unlimited.
		stageChanges: {
			title: 'Monthly stage change limit reached',
			description:
				"You've used all of this month's stage changes on the Free plan. Upgrade your plan for unlimited stage changes.",
		},
		stageChangesReferrer: {
			title: 'Monthly stage change limit reached',
			description:
				"This workspace has used all of this month's stage changes. Ask your workspace admin to upgrade for unlimited stage changes.",
		},

		linkedInLookup: [
			'Monthly credit limit reached. ',
			{ action: 'upgrade', label: 'Upgrade' },
			' for AI lookups.',
		],
		linkedInLookupReferrer: 'Monthly credit limit reached. Ask your workspace admin to upgrade.',
		linkedInLookupTip:
			'AI lookups fetch candidate info from public data sources, which may not be fully up to date. Each lookup uses ~$0.02 of credits.',
	},
	access: {
		adminOnly: 'Only visible to admins',
		adminOnlyAction: 'Only accessible to admins',
		manageAccess: (target: 'form' | 'page') => `You have manage access for this ${target}.`,
		/** Signed in, but not as someone this assignment or feedback link was sent to. */
		linkNoAccess: (kind: 'assignment' | 'feedback') =>
			`You don't have access to this ${kind} link. Please check that you're signed in with the right email address.`,
		restrictedTitle: 'Access restricted',
		workspaceDenied:
			"You either don't have access to this workspace or are currently in a different workspace.",
		pageDenied:
			"You don't have access to this page. This content is restricted to specific members within the workspace.",
		goToHome: 'Go to Home',
		accessDeniedTitle: 'Access denied',
		goHomeCta: 'Go home',
		formAdminsAlwaysAccess: 'Admins of the workspace can always access all forms',
		mobileUnavailable: 'Not available on mobile yet',
		desktopRecommended: 'For the best experience, please view this content on a desktop browser.',
		/** 401 on a form page. Split around a `<br />`; the tail carries its own leading space. */
		formManageDeniedLead: 'You don’t have permission to manage this form. Meanwhile,',
		formManageDeniedTail: ' you can still view and fill the form as a candidate.',
		formEditorsHelp: 'Editors can manage candidates and edit the form.',
		/** Backticks are kept: the alert dialog renders the backticked part in bold. */
		editorRemoveConfirm: (name: string) =>
			`Deleting \`${name}\` will remove their entire access for this role.`,
	},
	billing: {
		creditLimitLabel: 'Monthly credit limit',
		usageLabel: 'Current month usage',
		creditsExplainer:
			'Credits are needed for AI usage, sending emails and offer letters. They are shared across your entire team.',
		usageUsed: (used: number) => formatUsd(used),
		creditLimit: (limit: number) => formatUsd(limit),
		usageResets: (resetAt: string | number) =>
			`Resets ${formatEffectiveDate(resetAt, { includeYear: false })}`,
		usageSummary: (used: number, resetAt: string | number) =>
			`${formatUsd(used)} used. Resets ${formatEffectiveDate(resetAt, { includeYear: false })}.`,
		usageUnavailable: "Couldn't load",
		changeLimit: 'Change',
		showUsageCta: 'Show usage',
		usageEmailLabel: 'Email',
		usageAiLabel: 'AI',
		usageOfferLabel: 'Offer letters',
		storageLabel: 'Storage used',

		planLabel: 'Current plan',
		alreadySubscribedTitle: 'Already subscribed',
		alreadySubscribedBody: 'You already have an active subscription',
		extraCreditsCancel: 'Cancel',
		extraCreditsIncluded: 'Included for free',
		extraCreditsPriceTag: (amount: number, currency?: string) =>
			`Pay only ${formatWholeUsd(amount, currency)}`,
		/** Options name the total monthly credits, not the price charged. */
		extraCreditsIncludedOption: (total: number) => formatWholeUsd(total),
		extraCreditsOption: (total: number) => formatWholeUsd(total),
		extraCreditsInactiveLabel: 'Subscription inactive',
		extraCreditsInactiveTooltip:
			'Changing your monthly credits needs an active subscription. Update your payment method in Manage.',
		/** `tier` is the paid amount, which is also the credits it adds. */
		extraCreditsApplied: (tier: number) =>
			`Your extra ${formatUsd(tier)}/month of credits is active.`,
		/** Removal applied immediately: the workspace is back to its included credits. */
		extraCreditsRemoved: 'Your workspace is back to its included monthly credits.',
		/** Toast only, shown right after the action — never a persistent row. */
		extraCreditsScheduled: (tier: number | null, effectiveAt: string | number) =>
			tier === null
				? `Your extra credits stop renewing on ${formatEffectiveDate(effectiveAt)}.`
				: `Your extra credits change to ${formatUsd(tier)}/month on ${formatEffectiveDate(effectiveAt)}.`,
		/** Sending the user to Stripe — not a failure, so it must not read like one. */
		extraCreditsPaymentRequired: 'Taking you to checkout to confirm your new monthly credits.',
		/** PAYMENT_REQUIRED with no checkout URL: nothing to redirect to. */
		extraCreditsPaymentUrlMissing:
			'Could not open checkout. Please try again, or update your payment method in Manage.',
		extraCreditsFailed: 'Could not update your monthly credits. Please try again.',
		/** Charge summary before an increase: the saved card is charged with no Stripe page. */
		extraCreditsChargeLine: 'Extra credits',
		extraCreditsChargePerMonth: (amount: number) =>
			`${Number.isInteger(amount) ? formatWholeUsd(amount) : formatUsd(amount)} / month`,
		extraCreditsChargeMonthsLeft: 'Months left this billing year',
		extraCreditsChargeDueToday: 'Due today',
		extraCreditsChargeYearlyNote: (renewsAt: string | null) =>
			renewsAt
				? `Charged to your saved card. Plan renews ${formatEffectiveDate(renewsAt)}.`
				: 'Charged to your saved card.',
		extraCreditsChargeMonthlyNote: 'Charged to your saved card, then added to your monthly bill.',
		extraCreditsChargePreviewFailed:
			"We couldn't load the exact amount. Try again before paying.",
		extraCreditsPay: (amount: number) => `Pay ${formatUsd(amount)}`,

		upsellTitle: 'Choose your monthly credits',
		/** Billing: the workspace already has a limit, it is changing it. */
		limitChangeTitle: 'Choose your monthly credits',
		upsellContinue: 'Continue',
		upsellTotalPrice: (total: number, currency?: string) =>
			`${formatWholeUsd(total, currency)}/month total`,
		upsellPriceBreakdown: (
			planPrice: number,
			creditsPrice: number,
			total: number,
			currency?: string
		) =>
			`Plan ${formatWholeUsd(planPrice, currency)}/month + credits ${formatWholeUsd(creditsPrice, currency)}/month = ${formatWholeUsd(total, currency)}/month total.`,
		upsellPriceBreakdownLabel: 'View price breakdown',

		/** Overdue banner. The body is hidden on small screens, so it carries its own leading space. */
		overdueLead: 'Your payment is overdue.',
		overdueBody: ' If unpaid, you will lose access to paid features.',

		paymentVerifying: 'Please wait while we verify your payment',
		paymentAutoClose: (seconds: number) => `This page will auto close in ${seconds} seconds.`,
		paymentExpired: 'Your payment session has expired. Please try again.',
		paymentPending: 'Your payment is still being processed. Please check back later.',
		paymentUnknown: 'We could not determine the status of your payment.',
		paymentCancelHelp: (email: string) => `Write to us at ${email} if you need help.`,
	},

	confirms: {
		areYouSure: 'Are you sure?',
		cannotBeUndone: 'This action cannot be undone.',
		discardUnsavedChanges: 'You have unsaved changes. Are you sure you want to discard them?',
		deleteForm: (formTitle: string) => [
			'This will permanently delete the form ',
			{ em: formTitle },
			' and all its associated data.',
		],
	},

	/**
	 * Pricing-card copy. Feature lines are ordered as they render; the numbers in
	 * `{limit}` are substituted from `plan-limits`, and `href`/icon choices live in
	 * the pricing feature tables — this section holds only the words.
	 *
	 * Keep `text` to a short noun phrase — a card is scanned, not read. Detail,
	 * caveats and per-month wording belong in `tooltip`, which every line can carry.
	 * Do not repeat the tooltip's number or explanation in the label.
	 */
	pricing: {
		pageHeading: 'Pricing',
		pageDescription: 'No per-user pricing. Unlimited seats on every plan.',
		billingMonthly: 'Monthly',
		billingYearly: 'Yearly',
		billingYearlySave: 'Save 25%',
		compareFeatureColumn: 'Features',
		compareUnlimited: 'Unlimited',
		compareIncluded: 'Included',
		compareNotIncluded: 'Not included',
		/** Compare-table info icons; the same strings the card feature lines carry. */
		compareTooltipStageChanges: STAGE_CHANGES_TOOLTIP,
		compareTooltipCredits: CREDIT_USAGE_TOOLTIP,

		/** Switching offer under the compare table; the full story lives on /switch. */
		switchOfferTitle: 'Switching from another ATS? Your remaining months are free.',
		switchOfferDescription: (migrationHours: number) =>
			`If you have 6 months left on your current ATS contract, we will offer Binary free for 6 months on any annual plan. Data migration takes typically about ${migrationHours} hours if you have a big database; or you can do it instantly with AI yourself.`,
		switchHowItWorksCta: 'How switching works',
		switchTalkToUsCta: 'Talk to us about switching',
		freeFeatures: [
			{ text: 'Unlimited jobs' },
			{ text: 'Unlimited candidates' },
			{ text: 'AI agent, Slack agent' },
			{ text: 'Careers page' },
			{ text: 'Email candidates' },
			{ text: 'Chrome extension' },
			{ text: '{limit} free credit/month', tooltip: CREDIT_USAGE_TOOLTIP },
		],
		starterFeatures: [
			{ text: 'Everything in Free' },
			{ text: 'Unlimited stage changes', tooltip: STAGE_CHANGES_TOOLTIP },
			{ text: 'Duplicate detection' },
			{ text: 'Analytics' },
			{ text: 'Admin settings' },
			{ text: '{limit} free credits/month', tooltip: CREDIT_USAGE_TOOLTIP },
			{ text: 'Add-on credits' },
		],
		businessFeatures: [
			{ text: 'Everything in Starter' },
			{ text: 'Job board integration' },
			{ text: 'Interview scheduling' },
			{ text: 'Interview feedback' },
			{ text: 'Offer letters', tooltip: '{limit} offer letters per month' },
			{ text: 'Assignments' },
			{ text: 'Advanced analytics' },
			{ text: '{limit} free credits/month', tooltip: CREDIT_USAGE_TOOLTIP },
		],
		proFeatures: [
			{ text: 'Everything in Business' },
			{ text: 'Free data migration' },
			{ text: 'Unlimited offer letters' },
			{ text: 'Early access to new features' },
			{ text: 'Priority support' },
			{ text: '{limit} free credits/month', tooltip: CREDIT_USAGE_TOOLTIP },
		],

		planNameFree: 'Free',
		planNameStarterMonthly: 'Starter Monthly',
		planNameStarterYearly: 'Starter Yearly',
		planNameBusinessMonthly: 'Business Monthly',
		planNameBusinessYearly: 'Business Yearly',
		planNameProMonthly: 'Pro Monthly',
		planNameProYearly: 'Pro Yearly',

		/** Tier name without the billing interval — "Business", not "Business Monthly". */
		tierFree: 'Free',
		tierStarter: 'Starter',
		tierBusiness: 'Business',
		tierPro: 'Pro',

		perMonth: 'per month',
		/** Public pricing page CTA — the logged-in plans page uses `plans.planCtaUpgrade`. */
		getStartedCta: 'Get Started',
		loadError: "We couldn't load pricing right now. Please check your connection and try again.",

		creditsIncluded: (amount: number) => `${formatUsd(amount)}/month credits included`,
		creditsShared: 'Email, AI review, LinkedIn and offer letters all draw from these credits.',
		actionPriceEmail: `${formatUnitPrice(UNIT_PRICES_USD.emailRecipient)} per email recipient`,
		actionPriceReview: `${formatUnitPrice(UNIT_PRICES_USD.aiReview)} per AI review`,
		actionPriceLookup: `${formatUnitPrice(UNIT_PRICES_USD.linkedinLookup)} per LinkedIn fetch`,
	},

	plans: {
		/**
		 * Every plan gate uses one sentence, so a feature moving between tiers needs no
		 * copy edit. `isFreePlan` names the plan the user is actually on; everyone else
		 * gets the neutral "current plan".
		 */
		featureGate: (feature: string, isFreePlan: boolean) =>
			isFreePlan
				? `${feature} is not available in the free plan.`
				: `${feature} is not available in your current plan.`,
		featureGateReferrer: (feature: string) =>
			`${feature} is not available in your current plan. Ask your workspace admin to upgrade.`,

		/**
		 * Gate title. Defaults to "Paid feature": Starter is the entry paid tier, so
		 * naming it tells the user nothing except a word they must go look up. Pass a
		 * tier name only for a gate above that entry tier (e.g. Business).
		 */
		analyticsLocked: (planLabel = 'Paid') => `${planLabel} feature`,
		analyticsLockedDescription: 'to see detailed analytics',
		duplicatesLockedDescription: 'to see who they are',
		advancedSettingsLockedDescription: 'to set workspace defaults, stages and templates',

		/** Feature names fed to `featureGate`, so the wording stays identical. */
		featureAdvancedSettings: 'Changing advanced settings',
		featureReferrals: 'Referrals',
		featureFeedback: 'Feedback',
		featureTemplates: 'Editing templates',
		featureReferralSettings: 'Managing referral settings',
		featureReferCandidates: 'Referring candidates',
		featureSubmitReferrals: 'Submitting referrals',
		featureInviteReferrer: 'Inviting referrers',
		featureLinkedIn: 'Posting jobs to LinkedIn',

		referralsLockedToast: 'Upgrade to unlock referrals',
		referralsUnlockCta: 'Upgrade to unlock',
		upgradeFailed: 'Upgrade failed',
		upgradeCta: 'Upgrade',
		/** Logged-in plans page only — "Get Started" belongs to the public pricing page. */
		planCtaUpgrade: 'Upgrade',
		referrerUpgradeFallback: 'Ask your workspace admin to upgrade to access this feature.',
	},
	/** Shortlist / archive: what moving a candidate does, archive chat status, move results. */
	stageMoves: {
		archiveClosedSubText: 'The chat will be closed.',
		archiveClosedDescription: "Archived candidates won't be able to reply back to your message.",
		archiveOpenSubText: 'The chat will stay archived if they reply back.',
		archiveOpenDescription: 'Archived candidates will be able to reply back to your message.',
		archiveAppliesToForm: 'This will apply to all candidates in this form.',
		movedToStage: (count: number, stage: string) =>
			`${count} ${count === 1 ? 'candidate' : 'candidates'} moved to ${stage} successfully`,
		movedToStageTitle: (count: number, stage: string) =>
			`${count} ${count === 1 ? 'candidate' : 'candidates'} moved to ${stage}`,
		replyNudgeOn: 'Show me a nudge to reply when I shortlist/archive.',
		replyNudgeOff: 'Do not show any nudge when I perform an action.',
	},
	messaging: {
		bulkIndividualEmails:
			'Messages will be sent as individual emails; use {first_name} to personalize.',
		bulkReplyStatusTip: "To check people who have received a reply, use the filter 'Reply status'.",
		bulkAllRemainingReplied:
			'All remaining candidates with email addresses have already received a reply.',
		bulkAllSelectedReplied: 'All selected candidates have already received a reply.',
		bulkNoEmailToMessage:
			'No candidates to message. All remaining candidates have no email address.',
		bulkSkippedNoEmail: (count: number) =>
			count === 1
				? '1 candidate without email was skipped.'
				: `${count} candidates without emails were skipped.`,
		bulkUnableToSend: (count: number) =>
			count === 1
				? 'The candidate does not have an email address and hence the message could not be sent.'
				: 'The candidates do not have email addresses and hence the message could not be sent.',
		bulkSeparateEmail: '(everyone receives a separate email)',
		bulkChatClosed: 'Chat is closed. Cannot send messages.',

		chatClosedSubmitter: "You won't be able to reply to the conversation",
		/** Short status line under a closed chat — the dropdown option carries the full sentence. */
		chatClosedArchived: "Archived candidates won't be able to reply",
		addEmailLead: 'This candidate does not have an email address.',
		addEmailCta: 'Add email',
		addEmailTail: 'to start messaging.',
		addEmailExplainer:
			"Adding the candidate's email address lets you send emails to them directly via Binary.",
		noEmailToSchedule:
			"This candidate doesn't have an email address. Add an email to schedule an interview.",
		emptyChatPrompt: 'Send an email reply to the candidate.',
		replyGoesToEmail: 'Your reply will be sent to their email.',
		replyToSyncTip:
			'Replies to this email are automatically synced with the Binary inbox using a unique reply-to email address.',

		announceSaveEmail:
			'When you send a message from here, we send an email notification from a no-reply email.',
		announceChatsPage: 'Manage multiple conversations easily from the chats page.',
		announceQuickReplies: 'Save your most common replies and reuse them across conversations.',
		announceOneWay: 'When you archive the conversation, you can close it to prevent replies.',

		emailNotificationPending:
			'Message sent. We have also triggered an email notification which should be delivered in a few seconds.',
		emailNotificationDelivered:
			'Message sent. We also triggered an email notification which was successfully delivered.',
		emailNotificationDeliveredAt: (time: string) =>
			`Message sent. We also triggered an email notification which was successfully delivered at ${time}.`,
		emailNotificationFailed: (email: string) => [
			"We couldn't send an email notification to ",
			{ em: email },
			" because the address couldn't be found or is unable to receive email.",
		],
		emailNotificationFailedNote: (company: string) =>
			`Note that they can still see your message in their ${company} inbox.`,

		educationReplyInLead: 'Reply to this email directly or by opening it in',
		educationSmartEmailLead: 'Replies to this email are handled by a',
		educationSmartEmailTooltip: (company: string) =>
			`${company} creates a unique 'reply-to' email for each conversation so that replies are automatically synced to the ${company} dashboard.`,
		educationSmartEmailTail: 'to keep your email clean.',
		educationReplyForwarded: (company: string) =>
			`If someone replies back, it is sent to your email and also shows up in the ${company} inbox.`,
		educationArchivedReply: (company: string) =>
			`If an archived person replies back, it is NOT sent to your email and only shows up in the archived section of the ${company} inbox.`,

		deleteMessageBody:
			'Your message will be deleted but will still appear in the email notification that was sent.',
		deleteMessageWindow: 'Messages can only be deleted within 1 hour after they are sent',

		variableReplacedWith: (value: string) => `Will be replaced with: ${value}`,
		/** First-name tooltip in the chat footer, shown with the candidate's actual name. */
		firstNameReplacedWith: (name: string) => `This will be replaced with: ${name}`,
		variableReplaced: 'This variable will be replaced before sending.',
		firstNameHintLead: 'Use',
		firstNameHintTail: "for candidate's first name",
		assignmentLinkUnsupported: (token: string) =>
			`${token} isn't supported here and will be left blank.`,
		variablesHint: 'Type { to add a variable, like the candidate’s first name or yours.',

		/** Completes `quickReplyDefaultSuggestion` for each built-in template. */
		quickReplyHintArchive: 'when you archive someone',
		quickReplyHintShortlist: 'when you shortlist someone',
		quickReplyHintAssignment: 'when you send someone an assignment',
		quickReplyDefaultSuggestion: (hint: string) =>
			`This template will be the default suggestion ${hint}.`,
		quickReplyNameHelp: 'Internal name to identify this template. Eg: "Share meeting link"',
		quickReplyInfoTooltip:
			'Templates added here are specific to this form and can be used or edited by any of the form editors.',
		quickReplyCannotDelete: 'This template cannot be deleted.',
		quickReplyAlreadyAdding: 'You are already in the "Add" mode.',
		quickReplyDeleteConfirm: 'This template will be deleted permanently.',
		quickReplyNeedsAssignment:
			'Uses an assignment link, which only works when sending an assignment.',
	},
	assignments: {
		pageNotFound: 'This link is invalid or is no longer available.',
		pageAlreadySubmitted:
			'Thank you for submitting this assignment.\n\nAn assignment can only be submitted once. If you need to make changes, please contact the hiring team.',
		pageDeleted:
			'This assignment is no longer available.\n\nIf you still need to complete it, please contact the hiring team.',
		previewNotSubmittable: "This is a preview. Answers can't be submitted.",
		deleteSubmittedConfirm:
			"The candidate's submitted answers will be removed from this application.",
		deleteSentConfirm:
			"The assignment link will stop working immediately. The candidate won't be able to open or submit this assignment.",
		excludeAlreadySentTip:
			'They were already sent this assignment. Uncheck to send it to them again.',
		linkAutoAdded: 'Your message has no assignment link, so one will be added at the end.',
		alreadyReceived: 'This candidate already received this assignment.',
		/** Send-result detail lines; `reasons` is the API's failure reasons, already joined. */
		outcomeFailed: (count: number, reasons: string) => `${count} failed: ${reasons}.`,
		outcomeNotEmailed: (count: number) =>
			`${count} ${count === 1 ? 'candidate was' : 'candidates were'} recorded but not emailed.`,
		outcomeNoEmail: (count: number) =>
			`${count} ${count === 1 ? 'candidate' : 'candidates'} had no email address.`,
		outcomeAlreadySubmitted: (count: number) =>
			`${count} ${count === 1 ? 'candidate' : 'candidates'} had already submitted it.`,
		/** Nobody left to send to. `reasons` are the `reason*` keys below, in that order. */
		nobodyLeft: (isSingle: boolean, reasons: string[]) =>
			`${isSingle ? 'This candidate has' : 'Everyone selected has'} ${reasons.length > 1 ? 'either ' : ''}${reasons.join(' or ')}.`,
		reasonSubmitted: 'already submitted this assignment',
		reasonAlreadyReceived: 'already received this assignment',
		reasonNoEmail: 'no email address',
	},
	offers: {
		templatesHelp:
			'Applied to new forms as offer letter templates your team can send to candidates.',
		noEmail: 'Add an email first',
		liveOffer: 'This candidate already has an offer letter. Rescind it before sending a new one.',
		messageReady: 'The offer letter is ready. Send the candidate its link.',
		rescindConsequence:
			'The offer link will stop working right away and the candidate will see it is no longer active. You can send them a new one after.',
		pageAwaitingCountersign:
			'The company still needs to countersign it. The signed copy will be available here.',
		pageAccepted: 'Everyone has signed. Download a copy for your records.',
		pageDeclined: 'Contact the company if this was a mistake.',
		pageExpired: 'Contact the company if you still want to accept it.',
		pageRescinded: 'Reach out to the company if you have any questions.',
		pageInvalid: 'Check that you opened the full link you were sent.',
	},
	interviews: {
		feedbackLinkInvalid: 'This link may be invalid or expired. Please contact the recruiter.',
		deleteInterviewConfirm:
			'This will cancel the calendar event and permanently remove this interview.',
		defaultTemplateTip: 'This will be the default template when creating interviews.',
		titleAutoFilledTip: 'The name of the interview will be auto-filled in the title.',
		titleLockedTip: "Title is a mandatory field and can't be edited.",
		ratingLockedTip: "Overall rating is a mandatory field and can't be edited.",
		deleteTemplateConfirm:
			'This template will be permanently deleted. This action cannot be undone.',
		deleteFeedbackConfirm: 'This feedback will be permanently deleted.',
		deleteBookingLinkConfirm:
			"The candidate can no longer book a time. You'll need to send a new link if they still need to schedule.",
	},
	aiReview: {
		criteriaRemoved: (count: number) =>
			count === 1
				? "This criterion will be removed from all candidates and won't be used in future reviews."
				: "These criteria will be removed from all candidates and won't be used in future reviews.",
		criterionRunning:
			'This criterion is part of a running review. Wait for it to complete before deleting.',
		reviewInProgress: 'Review in progress. Pause the review to take this action.',
		pendingReview: 'Some candidates are pending review.',
		/** Followed by the pending criteria names. */
		pendingReviewFor: (count: number) =>
			`Some candidates are pending review for ${count === 1 ? 'this' : 'these'} criteria:`,
		queryRejected: "I couldn't understand your query. Please modify it and try again.",
		reviewAlreadyRunning: 'A review is already running for this form. Wait for it to finish first.',
		reviewFormNotFound: 'Form not found or you do not have access.',
		/** Toast when adding a criterion the API flags as a wrong query. */
		promptNotUnderstood:
			'I am having trouble understanding your request. Please modify your prompt and try again.',
	},
	aiAgent: {
		formMentionTooltip: 'The agent will use this form as context',
		/** First-time coachmark, on the agent FAB and above the panel input. */
		reviewNudge: {
			title: 'Too many candidates?',
			description: 'Let AI do the first review.',
		},
		/** Follows the keyboard-shortcut keys in both review-nudge coachmarks. */
		shortcutTryTail: 'to try',
		skillNameHelp: 'Internal name to identify this skill. Eg: "shortlist"',
		skillWhenToUseHelp: 'Let the agent know when to use this skill',
		newSkillDescription: 'Describe when this skill should apply.',
		newSkillBody: 'Write the instructions the agent should follow.',
		messageAlreadySent: 'This message was already sent.',
	},
	formSettings: {
		/** One draft story for all three publish-linked toggles: shown on, disabled, explained on hover. */
		draftAutoOn:
			'This will automatically turn on when you publish the form. You can turn it off later.',
		formLive: 'Your form is live and accepting responses.',
		notAcceptingSubmissions: 'The form is currently not accepting submissions.',
		/** Followed by the "here." popover trigger that opens the deadline picker. */
		deadlineLead: 'You can also set a form deadline by clicking ',
		/** `date` is already formatted. Followed by the "Change" popover trigger. */
		deadlineSet: (date: string) =>
			`The form will automatically stop accepting submissions on ${date}. `,
		oneSubmissionOn: 'When turned on, only one submission per email is allowed.',
		oneSubmissionOff: 'Only one submission per email is allowed.',
		oneSubmissionVerifyTip:
			'When submitting the form, a one-time verification code will be sent to their email.',
		verifyEmailHelp:
			'A one-time confirmation code will be sent to their email after clicking submit.',
		verifyEmailRequiredTip:
			'Email verification must be enabled when limiting to one submission per email.',
		/** Followed by the careers-page link, or "public link" when the workspace has no handle. */
		publicPageLead: 'When accepting submissions, show this form on',
		/** Replaces the careers-page link when the workspace has no handle; carries its leading space. */
		publicPageTail: ' public link',
		linkedInDelayTip:
			'LinkedIn picks up jobs on its own schedule, so a new job can take 6-12 hours to appear on your LinkedIn page.',
		linkedInPostHelp: 'Post this job to your LinkedIn company page and the LinkedIn Jobs board.',
		/** Both carry a trailing space: the "Customize" link may follow. */
		customMessageOn: 'Candidates see a custom message after submitting. ',
		customMessageOff: 'Show a custom message to candidates after they submit the form. ',
		customMessageHelp: 'This message is shown to candidates after they submit the form.',
		replyNotifHelp: 'Receive emails when candidates reply. Not applicable for archived candidates.',
		/** Followed by the "here" dropdown trigger that lists the reply recipients. */
		replyRecipientsLead: 'Manage recipients',
		replyRecipientsHelp:
			'The following people will receive emails whenever a candidate sends a reply.',
		themesDesktopOnly: 'Please use desktop to switch themes',

		/** Live-stats widgets: short tooltips in the editor, longer helpers in the widgets dialog. */
		widgetLocationTip: 'Show job location on the form.',
		widgetResponseRateTip: (threshold: number) =>
			`This widget will only show up on the form when your response rate is above ${threshold}%.`,
		widgetApplicantsTip: (threshold: number) =>
			`This widget will only show up on the form when the number of candidates is above ${threshold}.`,
		widgetLocationHelp: 'Show job location on the form. Select one or more cities.',
		widgetDeadlineHelp:
			'If turned on, the form will stop accepting submissions on the deadline. You can change the deadline anytime.',
		widgetApplicantsHelp: (threshold: number) =>
			`Show the total number of candidates. If turned on, this stat only starts showing once the form crosses ${threshold} candidates.`,
		widgetResponseRateHelp: (threshold: number) =>
			`Show how responsive you are. If turned on, this stat is only shown when your response rate is over ${threshold}%.`,
	},
	linkedIn: {
		/**
		 * `LINKEDIN_EXCLUSION_COPY` reasons. Each is a full sentence: it is the tooltip on both the
		 * warning icon beside "Publish to LinkedIn" and the locked toggle.
		 */
		exclusionMissingTitle: 'You need to add a job title to send this job to LinkedIn.',
		exclusionMissingDescription: 'You need to add a job description to send this job to LinkedIn.',
		exclusionUrlTooLong: "This job's URL is too long for LinkedIn. Contact support to fix it.",
		exclusionUrlMissing: "This job's URL is missing. Contact support to fix it.",
		exclusionNotConnected: 'Connect LinkedIn in workspace settings to send this job to LinkedIn.',
		exclusionPaidPlan: 'LinkedIn posting needs an active paid plan.',
		exclusionFormClosed: 'Turn on "Currently accepting submissions" to send this job to LinkedIn.',
		previewUnavailable:
			'Unable to show LinkedIn profile preview. This usually happens if the URL is wrong or the profile is private.',
		shareJobsHelp: 'Share your live jobs with LinkedIn.',
		excludeJobTip: `To keep a specific job off LinkedIn, turn off "Publish to LinkedIn" in that job's settings.`,
		connectSteps: 'Complete both steps to connect.',
		enableListingsHelp:
			'LinkedIn only accepts jobs from companies that have turned this on, so this is required for your jobs to appear on LinkedIn.',
		disconnectConfirm:
			'Your open jobs will no longer be shared to LinkedIn. You can reconnect anytime.',
	},
	fields: {
		/** Fallbacks when a locked field has no field-specific tooltip. */
		fieldNotEditable: 'This field cannot be edited',
		fieldNotDeletable: 'This field cannot be deleted',
		/** Locked system fields (name, email). Pass the field name as it should read. */
		lockedRequired: (field: string) => `${field} is always required`,
		lockedType: (field: string) => `Cannot change type for ${field}`,
		lockedDelete: (field: string) => `${field} cannot be deleted`,

		protectHelp: 'Hide this field from other editors or interviewers.',
		protectSelectedOnly: 'Only the selected people will see this field',
		protectAdminsAlwaysSee: 'Workspace admins will always see this field',
		protectOwnerAlwaysSees: 'Form owner can always see this field.',
		protectTip: 'Only show this field to specific people.',
		protectedBadge: 'This field is protected. Only allowed people can see it.',

		addColumnHelp: 'This column is only added to the table. It will not appear on your live form.',
		columnDeleteConfirm: (column: string) =>
			`"${column}" column will be permanently deleted for all editors of this form.`,
		/** Form field column: deleting it also removes a live field from the form. */
		fieldDeleteConfirm: (column: string) =>
			`"${column}" and its data will be removed for everyone with access to this form. If this field is live on your form, it will be removed from the form too.`,
		/** Second, final step of the form field column delete. */
		columnDeleteFinal: (column: string) => `Permanently delete "${column}"?`,

		stageNotEditable: 'This stage cannot be edited',
		stageNotDeletable: 'This stage cannot be deleted',
		stageHasCandidates: 'To delete, please move candidates to a different stage.',
		stageRenameConfirm: (from: string, to: string) => `You are renaming "${from}" to "${to}"`,

		videoEditModeTip: 'You are currently in edit mode. Open preview mode to try.',
		videoPermissionBlocked:
			'Please enable them by clicking on the info icon inside the browser address bar.',
		videoDeleteConfirm: 'The recorded video will be permanently deleted.',
		videoRetakeConfirm: 'The previous video will be discarded when you record a new one.',
		videoRecorded: (maxSeconds: number) =>
			`Your video has been recorded. We support videos up to ${maxSeconds} seconds in length.`,
		videoMaxLength: (maxSeconds: number) => `(max ${maxSeconds} seconds)`,
		videoUnsavedOnSubmit: (count: number) =>
			count > 1
				? `You recorded ${count} videos but didn't save them, so they won't be sent with your response.`
				: "You recorded a video but didn't save it, so it won't be sent with your response.",

		/** `getUserMedia` failures, keyed by error name at the call site. */
		mediaDenied:
			'Camera and microphone access was denied. Please allow permissions to record video.',
		mediaNotFound: 'No camera or microphone found on your device.',
		mediaInUse: 'Camera or microphone is already in use by another application.',
		mediaUnsupported: 'Camera does not support the requested settings.',
	},
	candidates: {
		hasNotesTip: 'You have added one or more notes to this candidate',
		repliedTip: 'You have sent one or more replies to this candidate.',
		importedTip: 'This candidate was imported by a form editor',
		possibleDuplicates: (count: number) =>
			count === 1
				? 'We found an existing profile which looks like the same candidate.'
				: `We found ${count} existing profiles which look like the same candidate.`,
		videoCandidateOnly: 'Can only be recorded by the candidate.',
		deleteConfirm: (count: number) =>
			`Are you sure? This will permanently remove ${count > 1 ? 'these candidates' : 'this candidate'} and all their data.`,
		deleteOneConfirm:
			'This will permanently delete this candidate and all associated data like messages and notes.',
		/** Followed by the target job's name and a full stop. */
		moveDuplicateLead: 'A submission with this email already exists in',
		/** Sits between the candidate's name and the target job's name, then a full stop. */
		movedToJob: 'has been moved to',
		viewSubmission: 'View submission',
		exportIntro: 'Your CSV export will include:',
		exportItemCandidates: 'All candidates',
		exportItemStages: 'Stage of the candidates (New, Shortlisted, etc)',
		exportItemAiReview: 'AI review status, if any ',
		exportExcludesMessages:
			"If you have sent messages to candidates or added comments, those messages/comments won't be exported.",
		responsesPaused:
			"We're working on loading candidates for this job faster. They'll be back here shortly, and all your data is safe. Your other jobs work as usual.",
		filtersDesktopOnly:
			'Sorry, filter options are not yet available on mobile. Please switch to desktop.',
		deleteLabelConfirm:
			'Deleting this label will remove it from all places where it is being used.',
		deleteCommentConfirm: 'This comment will be deleted permanently.',
		mentionNoEditors: 'No editors found. Add editors to this form to tag them here.',
	},
	referrals: {
		adminsOnlyEnable: 'Only admins can enable referrals',
		intro: 'Make it easy for employees and external recruiters to refer candidates.',
		removeReferrerConfirm: (email: string) =>
			`Are you sure you want to remove ${email} as a referrer? They will no longer be able to submit referrals.`,
		enableHelp: 'Turn on the referral portal so your team can start referring candidates.',
		formTitle: 'Referral form',
		formHelp: 'Choose the list of questions asked to the referrer about the candidate.',
		trackingHelp: 'If enabled, referrers will be able to see the current stage of the candidate.',
		linkTip: 'This link will automatically show all the live forms in your workspace.',
		linkAccessHelp: 'Only people allowed below can login and access this link.',
		/** Domain toggle when the workspace domain is unknown; with a domain it reads `workspace.domainJoinLead`. */
		domainAutoAllow: 'Auto-allow anyone from your email domain',
		inviteTip:
			"People you invite will be added as referrers. They won't have access to create or view forms.",
		adminSummaryTip:
			'This summary shows referrals made by everyone and is only visible to the admins of the workspace.',
		enableToConfigureForm: 'Enable referrals to configure the form',
		noCustomFields: 'No custom fields yet. Click "Add field" below to get started.',
		myReferralsEmpty: 'Candidates you refer will appear here.',
		inviteInvalid:
			"This invite link is invalid, or you don't have access to this workspace. Please contact the workspace admin for help.",
		alreadyMember: "You're already a member of this workspace.",
		addedToWorkspace: "You've been added to this workspace.",
	},
	workspace: {
		disconnectCta: 'Disconnect',
		disconnectingLabel: 'Disconnecting...',
		roleAdmin: 'Can access all forms created by anyone.',
		roleMember: 'Can only access forms they create or are invited to.',
		roleReferrer: 'Can only refer candidates and cannot create or access forms.',
		/** Tooltip beside the domain-join sentence — shorter than `roleMember`, different slot. */
		memberVisibilityTip: 'Members only see forms they have created or been added to.',
		/** "Anyone with the email **@domain** can join this workspace as a member." */
		domainJoinLead: 'Anyone with the email',
		domainJoinTail: 'can join this workspace as a member.',
		memberRemoveConfirm: (name: string) =>
			`${name} will lose access to all forms. Ownership of any forms created by them will be transferred to the workspace owner.`,
		referrerNeedsReferrals: 'Referrals must be enabled for this workspace',
		memberAlreadyExists: 'This user is already in the workspace',
		/** Followed by the member's email. */
		changingAccessLead: 'You are changing access for',

		connectorClaude: 'Use Binary from Claude.',
		connectorCodex: 'Use Binary from ChatGPT.',
		connectorMcp: 'Use Binary from any AI assistant.',
		googleCalendarHelp: 'Schedule interviews seamlessly.',
		googleCalendarDisconnectConfirm:
			'All calendars connected to your Google Workspace will be disconnected. You can reconnect anytime.',
		slackHelp: 'Ask questions to Binary directly from Slack.',
		slackDisconnectConfirm:
			'The bot will no longer be able to respond to mentions in your Slack workspace. You can reconnect anytime.',
		/** `SLACK_ERROR_MESSAGES`, keyed by the OAuth callback's error code. */
		slackAlreadyConnected: 'This Slack workspace is already connected to another workspace.',
		slackConnectFailed: 'Failed to connect Slack. Please try again.',
		slackDenied: 'Slack authorization was not completed.',
		slackPending: 'Slack authorization is pending admin approval.',

		handleAlreadyYours: 'This handle is already yours.',
		handleAvailable: 'This handle is available.',
		handleHelp: 'This handle will be used for your public link.',
		publicLinkTip: 'This link shows all live jobs and can be shared with candidates.',
		publicLinkSelectWorkspace: 'Select a workspace to configure its public link.',

		dailyBriefHelp:
			"Get an AI-generated summary of what's happening and what needs your attention.",
		dailyBriefSlackNeeded: 'Connect Slack from the Integrations tab to get your summary there.',
		availabilityHelp: 'Set the hours candidates can book interviews with you.',

		/** Workspace defaults for new forms. `noun` is passed as it should read. */
		addedToNewForms: (noun: string) => `${noun} added to every new form by default.`,
		addedOnFormCreate: (noun: string) =>
			`Every time a new form is created, these ${noun} will be added by default.`,
		assignmentsHelp: 'Applied to new forms as reusable assignments you can send to candidates.',
		feedbackTemplatesHelp:
			'Applied to new forms for interviewers to fill out when submitting feedback.',
		subjectLineHelp:
			'Subject line for the email notifications candidates receive when you message them.',
		interviewNameHelp:
			'Prefills the interview title when scheduling with a candidate. You can edit it each time.',
		stageChangePromptTitle: 'Show message nudge on stage change',
		stageChangePromptHelp: 'Shows up every time you move a candidate from one stage to another.',
		linkedInFetchTitle: 'Show AI lookup',
		linkedInFetchHelp:
			'Automatically fetches data from the web about candidates using some of their public profile links.',
		linkedInFetchOffConfirm:
			"We'll stop running AI lookups on new candidates, so AI review won't have lookup results for them. Results already looked up stay on candidates and are still used. You can turn this back on anytime.",
		/** Fallback tooltip for a template variable with no description. */
		variableInserts: (token: string) => `Inserts ${token}`,

		supportBanner: 'Our team is here to help! Click your profile icon to connect with support.',
	},
	imports: {
		selectFromDriveCta: 'Select Files from Google Drive',
		/** Drive recommendation: Lead [AI assistant] and [PDF previews] Tail — each label has a tooltip. */
		driveRecommendLead: 'We recommend importing your Google Drive files too for features like',
		driveRecommendTail: 'to work.',
		driveRecommendJoin: 'and',
		driveFeatureAi: 'AI assistant',
		driveFeatureAiTip:
			'You can ask the AI assistant to read through files and tag candidates automatically.',
		driveFeaturePdf: 'PDF previews',
		driveFeaturePdfTip:
			'PDF previews let you read documents directly in Binary without having to switch tabs or download files.',
		filesSelected: (selected: number, total: number) =>
			`${selected} out of ${total} file${selected !== 1 ? 's' : ''} from your CSV have been selected`,
		filesSelectedContinue: (count: number) =>
			`You can continue with ${count === 1 ? 'this 1 file' : `these ${count} files`}, or update your selection`,
		filesSelectedContinueMissing: (count: number) =>
			`You can continue with ${count === 1 ? 'this 1 file' : `these ${count} files`}, or update your selection to include the missing ones`,
		/** Drive picker steps: "Click the [Select Files] button to open the Google Drive file picker." */
		selectFilesCta: 'Select Files',
		drivePickerLead: 'Click the',
		drivePickerTail: 'button to open the Google Drive file picker.',
		driveSelectAll: 'Select all the files you want to import.',
		driveGoogleFormsFolder: 'If you used Google Forms, you will find them in one folder.',
		/** "(Hold **Shift + Click** to select multiple files)" */
		driveMultiSelectLead: '(Hold',
		driveMultiSelectKeys: 'Shift + Click',
		driveMultiSelectTail: 'to select multiple files)',
		driveMultiSelectTailAtOnce: 'to select multiple files at once)',

		googleFormOnlyHelp: 'Supports Google forms only',
		googleFormUrlTip:
			'If you have an existing form, we will create an identical form on Binary so that you can share it with future candidates.',
		googleFormSignIn:
			'This generally happens when your Google form requires a sign-in. Make the form public and try again.',
		csvHeadingsRequired: 'Make sure the first row has headings. Name column is required.',
		headerRowOnly:
			'0 candidates found. File contains only one row which will be used as a header row.',
		fieldMappingTip: 'Name is required. Email is recommended for messaging feature to work.',
		newFieldsCreated: (count: number) =>
			`${count} new ${count === 1 ? 'field' : 'fields'} will be created`,
		oneColumnPerField: 'Each form field can only be mapped to one CSV column.',
		mapNameColumn: (field: string) =>
			`Map a CSV column to the "${field}" field — it's required to import candidates.`,
		noNameField: "This form has no Name field, so candidates can't be imported.",
		statusCheckFailed:
			"We couldn't check the import status. Your import may still be processing in the background.",
	},
	analytics: {
		/** Appended to the helper of every metric that only counts form applicants. */
		formApplicantsOnly: 'Only includes candidates who applied via the form.',
		totalViewsHelp: 'Total number of times this form has been viewed.',
		totalCandidatesHelp: 'Total candidates across all forms.',
		totalSubmissionsHelp: 'Total number of submissions received.',
		reviewRateHelp:
			'Percentage of candidates who were moved from New to any other stage at least once.',
		responseRateHelp: 'Percentage of candidates who received at least one reply.',
		avgReviewTimeHelp:
			'Average time taken to move a candidate from New to any other stage for the first time.',
		avgResponseTimeHelp: 'Average time taken to send the first response to a candidate.',
		/** Shared by the views-and-submissions and form-conversion charts. */
		viewsToSubmissionsHelp: 'The number of people who viewed your form and actually submitted it.',
		candidateFunnelHelp:
			'Number of candidates who have ever been part of a stage. If you move a candidate from New to Shortlisted to Archived, they will be counted in all three stages.',
		avgTimeToReachStageHelp:
			'Average time taken for a candidate to reach this stage from the time they were added.',
		emptyExplainer: 'Analytics will appear here once you have candidates in your workspace',
	},
	onboarding: {
		nameTitle: 'What should we call you?',
		nameHelp: "Let's start by getting to know you",
		workspaceHelp: 'Create your workspace to get started',
		workspaceIntro:
			'Workspaces are shared environments where you can create forms and invite your team.',
		joinTeamHelp: 'Join them in their existing workspace.',
		nameScreenHelp: "We'll set up your workspace while we're at it.",
		workspaceNameHelp: 'This is generally the name of your company or team.',
		inviteInvalid:
			'This invite link is invalid or has expired. You can create a new workspace instead.',
		/** Followed by the provisioning error message. */
		provisionFailed: "We couldn't set up your workspace.",
		templateToForm: 'Like this template? Turn it into a form.',
		templateNotSubmittable: "Templates can't be submitted.",
	},
	mcpConsent: {
		invalidRequest:
			'This authorization request is invalid or has expired. Please restart the connection from the app.',
		/** Followed by the member's email, then `actAsTail`. */
		actAsLead: (client: string) => `${client} will be able to act as`,
		actAsTail: '. You can disconnect anytime.',
		unverifiedApp: (client: string) =>
			`${client} is an unverified app. Only continue if you trust it.`,
		loopbackNotice: 'Only approve if you started this connection yourself just now.',
		restartConnection: (client: string) => `Please restart the connection from ${client}.`,
	},
	careers: {
		descriptionTrimmed: 'Description was too long, so it was shortened to fit.',
		maxPinned: (max: number) => `Maximum ${max} pinned jobs allowed`,
	},
	changelog: {
		entryNotFound: "That changelog entry wasn't found",
		feedIntro: 'New features, improvements, and fixes.',
	},
	scheduling: {
		timesShownIn: (timezone: string) => `Times shown in ${timezone}`,
		viewAvailability: 'View availability',
		calendarNotConnected: 'Calendar not connected',
		connectCalendarCta: 'Connect',
		calendarNotConnectedHint:
			'Once connected, you can see available slots and send booking links to candidates.',
		connectSelf:
			'Create events, send booking links and avoid scheduling conflicts by connecting your calendar.',
		nudgeInterviewer:
			'Send a link to the interviewer to connect their google calendar and set their availability.',
		nudgeInterviewerForAvailability:
			'Send a link to the interviewer to connect their google calendar so you can see their available slots.',
		connectSelfForAvailability:
			'See your available slots and avoid scheduling conflicts by connecting your calendar.',
		busyLoadFailed: "Couldn't load calendar",
		legendPreferred: 'Preferred hours',
		legendBusy: 'Busy',
		prevWeek: 'Previous week',
		nextWeek: 'Next week',
		slotTooltip: (kind: string, range: string) => `${kind} · ${range}`,

		calendarPrivacy:
			'Binary will never access any other event on your Google Calendar other than the events created via the platform.',
		calendarSyncReason:
			'This is needed to create the event and maintain a 2-way sync with Google Calendar in case any changes are made to the event.',
		calendarRequiredForLink:
			'Your Google Calendar must be connected so Binary can place events in it.',
		calendarRequiredToSchedule: 'Please connect your Google Calendar before scheduling.',
		interviewerCalendarsRequired:
			"Every interviewer's calendar must be connected before creating a scheduling link.",
		/** Follows the `connectCalendarCta` button in the schedule form's calendar banner. */
		connectToCreateTail: 'your Google Calendar to create the interview.',
		inviteCreatedVia: 'The calendar invite will be created via',
		bookingLinkNeedsInterviewer: 'Add an interviewer to create booking link.',
		bookingLinkNeedsCalendar: "Connect the interviewer's calendar to create booking link.",
		/** Wraps the slot-duration picker: "Create a link … book a [30 min] slot." */
		bookingLinkLead: 'Create a link for the candidate to book a',
		bookingLinkTail: 'slot.',
		bookingPreviewHint: 'This is what the candidate will see when they open the link.',
		meetLinkAutoAdded: 'Google Meet link auto added',
		lockedAfterScheduling: "Can't be changed after scheduling",
		feedbackLinkTip:
			'This link will be added to the calendar invite description and will need a login from the interviewer to access.',
		candidateLocked: 'Candidate — cannot be removed',
		interviewerSeesIntro: 'On clicking the link, interviewer will see:',
		visibilityCandidateDetails: 'All candidate fields except protected fields and comments.',
		visibilityFeedbackForm:
			'Interviewers fill out this form after the interview. Automated reminders are sent until they submit it.',

		orgCalendarCoversSelf:
			'Covers every teammate. You can also connect just your personal calendar above.',
		orgCalendarCoversInterviewer:
			'If this person belongs to your workspace, they should already be covered.',
		orgCalendarSetupHint:
			'One time setup that makes it easy to create booking links for any interviewer.',
		orgCalendarIntro:
			'Allow Binary to manage interviews and check for available time slots for any interviewer in the company.',
		/** "You need to be a Google Workspace **super admin** to complete this setup." */
		orgCalendarSuperAdminLead: 'You need to be a Google Workspace',
		orgCalendarSuperAdminRole: 'super admin',
		orgCalendarSuperAdminTail: 'to complete this setup.',
		orgCalendarPropagation: 'Google changes may take up to 5 minutes to propagate.',
		orgCalendarConnectFailed:
			'We could not connect to your Google workspace. Please try again after some time.',
		orgCalendarVerifyFailed:
			'Verification failed. It can take up to 5 minutes for Google to propagate changes. Please wait and try again.',
		/** Followed by the connected domain names, then `orgCalendarConnectedTail`. */
		orgCalendarConnectedLead: (domainLabel: string) => `All calendars in the ${domainLabel}`,
		orgCalendarConnectedTail: 'are now connected.',

		availabilityNotConnected: "No calendar connected. Binary never reads events it didn't create.",
		availabilityHiddenEvents:
			'Existing events on your calendar will automatically be hidden from the available time slots shown to candidates.',

		bookingAlreadyUsed: 'An interview has already been scheduled through this link.',
		bookingCancelledContact: (workspaceName: string) =>
			`Please contact ${workspaceName} if you still need to book a time.`,
		/** Followed by the booked time and a full stop. */
		bookingInviteSentLead: 'A calendar invite has been sent to your email for',
	},
	candidateModal: {
		resumeAutofillPending: 'Autofilling other fields…',
		resumeAutofillSuccess: (count: number) =>
			`Filled ${count} field${count === 1 ? '' : 's'} from resume`,
	},
} as const satisfies CopyTree;
